import {
  Box,
  Pagination as DefaultPagination,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PaginationItem } from "@mui/material";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { useTranslation } from "react-i18next";
import { PaginationBlock, PaginationStyled } from "./styled";
import { hexToRgba } from "@/Components/helpers/util";

type Props = {
  id?: string;
  total: number;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (pagination: { pageSize: number; page: number }) => void;
  itemCount?: number;
};

export default function Pagination(props: Props) {
  const { t } = useTranslation(["common", "apiGovernance"]);

  const {
    id = "snr-ai-pagination",
    total,
    paginationModel,
    setPaginationModel,
    itemCount,
  } = props;

  const page = paginationModel?.page || 1;
  const size = paginationModel?.pageSize || 10; // Default to 10 if undefined

  const rowsPageList = [
    { label: 10, val: 10 },
    { label: 15, val: 15 },
    { label: 20, val: 20 },
    { label: 25, val: 25 },
  ];

  return (
    <PaginationBlock gap={5}>
      {total > 0 && (
        <Typography fontSize={"13px"} fontWeight={400} lineHeight={"24px"}>
          {total <= 10
            ? `${t("Showing")} ${total} ${t("out of")} ${total} ${t("Records")}`
            : `${t("Showing")} ${itemCount || size} ${t("out of")} ${total} ${t(
                "Records"
              )}`}
        </Typography>
      )}

      {total > 10 && (
        <>
          <Stack direction="row" alignItems="center" gap={2}>
            <TextField
              id={`${id}-rows-per-page`}
              size="small"
              select
              value={size}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                if (setPaginationModel) {
                  setPaginationModel({
                    pageSize: newSize,
                    page: 1,
                  });
                }
              }}
              sx={{ fontSize: 13 }}
              SelectProps={{
                IconComponent: (prop) => (
                  <ArrowDownwardOutlinedIcon
                    id={`${id}-rows-per-page-icon`}
                    {...prop}
                  />
                ),
              }}
            >
              {rowsPageList.map((option) => (
                <MenuItem
                  id={`${id}-rows-per-page-item-${option.val}`}
                  key={option.val}
                  value={option.val}
                  sx={{
                    fontSize: "13px",
                    "&.Mui-selected": {
                      background: (theme) =>
                        hexToRgba(theme.palette.primary.main, 0.1),
                      color: "inherit",
                    },
                  }}
                >
                  {option.label} {t("per page")}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" alignItems="center" gap={2}>
            <PaginationStyled>
              <DefaultPagination
                id={`${id}-pagination`}
                shape="rounded"
                count={Math.ceil(total / size) || 1} // Ensure count is correctly calculated
                variant="outlined"
                page={page}
                onChange={(_, pageNumber) => {
                  setPaginationModel({
                    pageSize: paginationModel?.pageSize,
                    page: pageNumber,
                  });
                }}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <ArrowBackOutlinedIcon id={`${id}-pagination-icon`} />
                          <Typography
                            fontSize={"13px"}
                            fontWeight={400}
                            lineHeight={"24px"}
                          >
                            {t("Prev")}
                          </Typography>
                        </Box>
                      ),
                      next: () => (
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <Typography
                            fontSize={"13px"}
                            fontWeight={400}
                            lineHeight={"24px"}
                          >
                            {t("Next")}
                          </Typography>
                          <ArrowBackOutlinedIcon
                            id={`${id}-pagination-icon`}
                            style={{
                              transform: "rotate(180deg)",
                            }}
                          />
                        </Box>
                      ),
                    }}
                    sx={{
                      "&.Mui-selected": {
                        color: "#fff",
                        border: "none",
                        background: (theme) => `${theme.palette.primary.main}`,
                      },
                    }}
                  />
                )}
              />
            </PaginationStyled>
          </Stack>
        </>
      )}
    </PaginationBlock>
  );
}
